import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { getAnalyticsOverview } from "../../api/admin.api";
import { isUnauthorizedError } from "../../api/http";
import AdminBreadcrumbs from "../../components/admin/AdminBreadcrumbs";
import EmptyState from "../../components/ui/EmptyState";
import ErrorState from "../../components/ui/ErrorState";
import LoadingState from "../../components/ui/LoadingState";
import AdminLayout from "../../layouts/AdminLayout";
import normalizeApiError from "../../utils/normalizeApiError";

const useCountUp = (value, duration = 800) => {
  const [animated, setAnimated] = useState(0);

  useEffect(() => {
    if (typeof value !== "number" || Number.isNaN(value)) {
      setAnimated(0);
      return;
    }
    let frameId = null;
    const start = performance.now();
    const startValue = 0;
    const endValue = value;

    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const nextValue = Math.round(startValue + (endValue - startValue) * eased);
      setAnimated(nextValue);
      if (progress < 1) {
        frameId = requestAnimationFrame(tick);
      }
    };

    frameId = requestAnimationFrame(tick);
    return () => {
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [value, duration]);

  return animated;
};

const TotalCard = ({ label, value, icon, tooltip, tone }) => {
  const animatedValue = useCountUp(typeof value === "number" ? value : 0);
  const tooltipId = `${label.replace(/\s+/g, "-").toLowerCase()}-tooltip`;

  return (
    <div
      className={`card group relative border ${tone} p-4`}
      tabIndex={0}
      aria-describedby={tooltipId}
    >
      <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wide">
        <span>{label}</span>
        <span className="text-lg" aria-hidden="true" title={`${label} icon`}>
          {icon}
        </span>
      </div>
      <p className="mt-2 text-2xl font-semibold text-slate-900">
        {typeof value === "number" ? animatedValue : "—"}
      </p>
      <div
        id={tooltipId}
        className="pointer-events-none absolute left-1/2 top-full z-10 hidden w-52 -translate-x-1/2 rounded-lg bg-slate-900 px-3 py-2 text-xs text-white shadow-lg group-hover:block group-focus-within:block"
      >
        {tooltip}
      </div>
    </div>
  );
};

const formatTimestamp = (value) => {
  if (!value) return "No activity";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "No activity";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function AdminOverview() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const requestedRef = useRef(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAnalyticsOverview();
      setData(response || null);
    } catch (err) {
      if (isUnauthorizedError(err)) {
        setError(normalizeApiError(err));
        return;
      }
      setError(normalizeApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (requestedRef.current) return;
    requestedRef.current = true;
    fetchData();
  }, []);

  const totals = data?.totals || {};
  const lastActivity = data?.lastActivity || {};
  const analysisStatus = data?.analysisStatus || {};
  const totalCards = useMemo(
    () => [
      {
        key: "clients",
        label: "Clients",
        value: totals.clients,
        icon: "👥",
        tooltip: "Total number of clients in the system",
        tone: "border-emerald-200 bg-emerald-50 text-emerald-700",
      },
      {
        key: "reports",
        label: "Reports",
        value: totals.reports,
        icon: "📄",
        tooltip: "Total number of reports generated",
        tone: "border-indigo-200 bg-indigo-50 text-indigo-700",
      },
      {
        key: "analyses",
        label: "Analyses",
        value: totals.analyses,
        icon: "📊",
        tooltip: "Total number of analyses created",
        tone: "border-purple-200 bg-purple-50 text-purple-700",
      },
      {
        key: "payments",
        label: "Payments",
        value: totals.payments,
        icon: "💳",
        tooltip: "Total number of payments recorded",
        tone: "border-amber-200 bg-amber-50 text-amber-700",
      },
      {
        key: "properties",
        label: "Properties",
        value: totals.properties,
        icon: "🏠",
        tooltip: "Total number of properties tracked",
        tone: "border-sky-200 bg-sky-50 text-sky-700",
      },
    ],
    [totals]
  );

  const activeCount =
    typeof analysisStatus.active === "number" ? analysisStatus.active : 0;
  const completedCount =
    typeof analysisStatus.completed === "number" ? analysisStatus.completed : 0;
  const totalAnalyses = activeCount + completedCount;
  const completedPercent = totalAnalyses
    ? Math.round((completedCount / totalAnalyses) * 100)
    : 0;

  const activeBadgeTone =
    activeCount > 0
      ? "bg-amber-100 text-amber-700"
      : "bg-slate-100 text-slate-500";
  const completedBadgeTone =
    completedCount > 0
      ? "bg-emerald-100 text-emerald-700"
      : "bg-slate-100 text-slate-500";

  return (
    <AdminLayout title="Admin Overview">
      <div className="space-y-6">
        <AdminBreadcrumbs />
        <div className="card p-4 text-sm text-slate-600">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span>Read-only analytics overview.</span>
            <NavLink
              className="text-xs text-slate-400 underline underline-offset-2"
              to="/admin/debug"
              data-testid="admin-debug-link"
            >
              Debug
            </NavLink>
          </div>
        </div>

        {loading ? (
          <LoadingState variant="cards" rows={5} />
        ) : error ? (
          <ErrorState error={error} onRetry={fetchData} />
        ) : !data ? (
          <EmptyState
            title="No overview data"
            message="Analytics overview is not available yet."
            testId="admin-empty-overview"
          />
        ) : (
          <>
            {/* Icons, animated counts, and tooltips for total metrics */}
            <section className="space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-slate-900">Totals</h2>
                <span className="text-xs text-slate-500">
                  Updated from analytics overview
                </span>
              </div>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                {totalCards.map((card) => (
                  <TotalCard
                    key={card.key}
                    label={card.label}
                    value={card.value}
                    icon={card.icon}
                    tooltip={card.tooltip}
                    tone={card.tone}
                  />
                ))}
              </div>
            </section>

            <section className="grid gap-4 lg:grid-cols-2">
              {/* Human-readable timestamps with null-safe fallbacks */}
              <div className="card p-4">
                <h2 className="text-sm font-semibold text-slate-900">
                  Last Activity
                </h2>
                <div className="mt-4 space-y-3 text-sm text-slate-600">
                  <div className="flex items-center justify-between">
                    <span>Payment</span>
                    <span className="font-medium text-slate-900">
                      {formatTimestamp(lastActivity.paymentAt)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Analysis</span>
                    <span className="font-medium text-slate-900">
                      {formatTimestamp(lastActivity.analysisAt)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Color-coded badges for analysis status (active/completed/zero) */}
              <div className="card p-4">
                <h2 className="text-sm font-semibold text-slate-900">
                  Analysis Status
                </h2>
                <div className="mt-4 space-y-4">
                  <div className="flex items-center gap-2">
                    <span
                      className={`rounded-full px-2 py-1 text-xs font-semibold ${activeBadgeTone}`}
                    >
                      Active {activeCount}
                    </span>
                    <span
                      className={`rounded-full px-2 py-1 text-xs font-semibold ${completedBadgeTone}`}
                    >
                      Completed {completedCount}
                    </span>
                  </div>
                  <div>
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span>Completion rate</span>
                      <span>{totalAnalyses ? `${completedPercent}%` : "—"}</span>
                    </div>
                    <div className="mt-2 h-2 w-full rounded-full bg-slate-100">
                      <div
                        className="h-2 rounded-full bg-emerald-400 transition-all"
                        style={{ width: `${completedPercent}%` }}
                      />
                    </div>
                    <p className="mt-2 text-xs text-slate-500">
                      {totalAnalyses
                        ? `${completedCount} of ${totalAnalyses} analyses completed`
                        : "No analysis activity"}
                    </p>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
